import api from './server'
import type {
  BulkCreateDiscussionRequest,
  BulkCreateDiscussionResponse,
  CopyDiscussionsRequest,
  CopyDiscussionsResponse,
  CreateDiscussionRequest,
  Discussion,
  DiscussionBody,
  DiscussionId,
  ListDiscussionsParams,
  PaginatedDiscussions,
  ReactDiscussionRequest,
  ReactDiscussionResponse,
  UpdateDiscussionRequest,
} from 'types/discussion'

/**
 * Discussion 'body' from API is JSON string format but parsed to object into react components
 */
type WireDiscussion = Omit<Discussion, 'body' | 'replies'> & {
  body: string | null
  replies: WireDiscussion[] | null
}

type WirePaginatedDiscussions = Omit<PaginatedDiscussions, 'results'> & {
  results: WireDiscussion[]
}

const safeParseDiscussionBody = (
  raw: string | null,
  id: DiscussionId,
): DiscussionBody | null => {
  if (raw == null) return null
  try {
    return JSON.parse(raw) as DiscussionBody
  } catch (err) {
    console.error(
      `[discussions] failed to parse body for discussion ${id}; rendering as null.`,
      err,
    )
    return null
  }
}

/** Parses 'body' (and recursively 'replies[].body') from JSON string to object. */
const parseBody = (wire: WireDiscussion): Discussion => ({
  ...wire,
  body: safeParseDiscussionBody(wire.body, wire.id),
  replies: wire.replies ? wire.replies.map(parseBody) : null,
})

const hydrateTargetType = <D extends Discussion>(
  discussion: D,
  target_type: string,
): D => ({
  ...discussion,
  target_type,
  replies: discussion.replies
    ? discussion.replies.map(r => hydrateTargetType(r as D, target_type))
    : null,
})

/**
 * GET /api/discussions/
 * Lists top-level discussions for a target. Offset-based pagination,
 * ordered by created desc. Generic over target type — caller passes
 * `target_type` (e.g. 'track') and `target_object_id`.
 */
export const fetchDiscussions = async (
  params: ListDiscussionsParams,
): Promise<PaginatedDiscussions> => {
  const wire = await api.getUrl<WirePaginatedDiscussions>(
    `${api.host}/api/discussions/`,
    params,
    'api.fetchDiscussions',
  )
  return {
    ...wire,
    results: wire.results.map(parseBody),
  }
}

/**
 * GET /api/discussions/{id}/
 * Retrieves a single discussion with nested replies.
 */
export const fetchDiscussionById = async (
  id: DiscussionId,
): Promise<Discussion> => {
  const wire = await api.getUrl<WireDiscussion>(
    `${api.host}/api/discussions/${id}/`,
    null,
    'api.fetchDiscussionById',
  )
  return parseBody(wire)
}

/**
 * POST /api/discussions/
 * Creates a top-level discussion or reply (when `parent` is supplied).
 */
export const createDiscussion = async (
  data: CreateDiscussionRequest,
): Promise<Discussion> => {
  const wire = await api.postUrl<WireDiscussion>(
    `${api.host}/api/discussions/`,
    { ...data, body_input: JSON.stringify(data.body_input) },
    'api.createDiscussion',
  )
  return parseBody(wire)
}

/**
 * POST /api/discussions/bulk_create/
 * Creates the same discussion across multiple targets
 */
export const createBulkDiscussions = (
  data: BulkCreateDiscussionRequest,
): Promise<BulkCreateDiscussionResponse> =>
  api.postUrl<BulkCreateDiscussionResponse>(
    `${api.host}/api/discussions/bulk_create/`,
    { ...data, body_input: JSON.stringify(data.body_input) },
    'api.createBulkDiscussions',
  )

/**
 * POST /api/discussions/copy/
 * Copies selected discussions (by id) from a source target to multiple targets.
 */
export const copyDiscussions = (
  data: CopyDiscussionsRequest,
): Promise<CopyDiscussionsResponse> =>
  api.postUrl<CopyDiscussionsResponse>(
    `${api.host}/api/discussions/copy/`,
    data,
    'api.copyDiscussions',
  )

/**
 * PATCH /api/discussions/{id}/
 */
export const updateDiscussion = async (
  id: DiscussionId,
  data: UpdateDiscussionRequest,
): Promise<Discussion> => {
  const wire = await api.patchUrl<WireDiscussion>(
    `${api.host}/api/discussions/${id}/`,
    { ...data, body_input: JSON.stringify(data.body_input) },
    'api.updateDiscussion',
  )
  return parseBody(wire)
}

/**
 * DELETE /api/discussions/{id}/
 * Hard delete: BE removes the record and returns 204 No Content.
 */
export const deleteDiscussion = (id: DiscussionId): Promise<void> =>
  api.delUrl(`${api.host}/api/discussions/${id}/`, 'api.deleteDiscussion')

/**
 * POST /api/discussions/{id}/react/
 * Toggles a reaction. Returns the updated reactions array.
 */
export const toggleDiscussionReaction = (
  id: DiscussionId,
  data: ReactDiscussionRequest,
): Promise<ReactDiscussionResponse> =>
  api.postUrl(
    `${api.host}/api/discussions/${id}/react/`,
    data,
    'api.toggleDiscussionReaction',
  )
